import { useQuery } from '@tanstack/react-query';
import { formatMoney, type InstalmentChoice, toCents } from '@k-management/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';

/** The instalments shown before «Mostra tutte»: the proposed ones and the oldest. */
const SHOWN = 6;

/**
 * «Rata di una rateazione» (3.15): the instalments not paid yet, the one of
 * the amount of the movement near its due date proposed and chosen; for a
 * card spending (3.21) with the commission inside.
 */
export function InstalmentDuesPicker({
  movement,
  card = false,
  url,
  onChange,
}: {
  movement: { id: string };
  card?: boolean;
  /** Where the choices come from, for what is neither a movement nor a spending (3.23). */
  url?: string;
  /** The instalments chosen and their cents. */
  onChange: (dueIds: string[], cents: number) => void;
}) {
  const choices = useQuery({
    queryKey: ['treasury', 'instalment-plans', 'choices', card, url, movement.id],
    queryFn: () =>
      apiRequest<InstalmentChoice[]>(
        url ??
          (card
            ? `/treasury/cards/movements/${movement.id}/instalment-choices`
            : `/treasury/instalment-plans/choices/${movement.id}`),
      ),
  });
  const [chosen, setChosen] = useState<string[]>([]);
  const [all, setAll] = useState(false);
  useEffect(() => {
    const proposed = (choices.data ?? []).find((item) => item.proposed);
    setChosen(proposed ? [proposed.dueId] : []);
  }, [choices.data]);
  useEffect(() => {
    const cents = (choices.data ?? [])
      .filter((item) => chosen.includes(item.dueId))
      .reduce((sum, item) => sum + toCents(item.amount), 0);
    onChange(chosen, cents);
  }, [chosen, choices.data, onChange]);

  if (choices.isLoading) return <p className="muted">Caricamento…</p>;
  if (choices.error) return <p className="error">{choices.error.message}</p>;
  if (!choices.data?.length) {
    return (
      <p className="muted small">
        Nessuna rata da pagare: crea prima la rateazione in{' '}
        <Link to="/tesoreria/rateazioni">Rateazioni</Link>.
      </p>
    );
  }
  return (
    <div className="stack tight">
      <small className="muted">Rate non ancora pagate:</small>
      {(all ? choices.data : choices.data.slice(0, SHOWN))
        .concat(all ? [] : choices.data.slice(SHOWN).filter((item) => chosen.includes(item.dueId)))
        .map((item) => (
          <label key={item.dueId} className="checkbox">
            <input
              type="checkbox"
              checked={chosen.includes(item.dueId)}
              onChange={(event) =>
                setChosen(
                  event.target.checked
                    ? [...chosen, item.dueId]
                    : chosen.filter((id) => id !== item.dueId),
                )
              }
            />{' '}
            <span>
              <strong>Rata {item.number}</strong> del {formatDate(item.dueDate)} ·{' '}
              {formatMoney(item.amount)} · {item.planName}{' '}
              <span className="muted">({item.subjectName})</span>{' '}
              {item.proposed && <span className="pill info">proposta</span>}
            </span>
          </label>
        ))}
      {!all && choices.data.length > SHOWN && (
        <button type="button" className="link small" onClick={() => setAll(true)}>
          Mostra tutte le {choices.data.length} rate da pagare
        </button>
      )}
    </div>
  );
}
